import React from "react";
import { useTranslation } from "react-i18next";
import { Unavailability } from "@planzone/dto";
import classNames from "classnames";
import { UseCases } from "@planzone/core";
import { useModal } from "@root/client/modal";

import {
  useAuthentication,
  useEditUnavailability,
  useRetrieveUnavailabilityReasons,
} from "@planzone/view-models";
import { Button } from "@common/button";
import { Modal } from "@common/modal";
import { UnavailabilityEditionForm } from "./unavailabilityEditionForm";
import { Loader } from "@common/loader";
import { ContextualMenu } from "@common/contextualMenu";
import { TrashIcon } from "@common/trashIcon";

import { Props } from "./types";
import styles from "./styles.scss";

function UnavailabilityEdition({ unavailability, cancel }: Props) {
  const { t } = useTranslation();
  const { pushView } = useModal();
  const { account } = useAuthentication();
  const [formData, setFormData] =
    React.useState<Unavailability>(unavailability);
  const { error, isRequestPending, editUnavailability } =
    useEditUnavailability(unavailability);
  const {
    isRequestPending: isUnavailabilityReasonsRequestPending,
    isRequestSuccess: isUnavailabilityReasonsRequestSuccess,
    isRequestFailure: isUnavailabilityReasonsRequestFailure,
    unavailabilityReasons,
    retrieveUnavailabilityReasons,
  } = useRetrieveUnavailabilityReasons();
  const loading = isUnavailabilityReasonsRequestPending;

  React.useEffect(() => {
    !isUnavailabilityReasonsRequestPending &&
      !isUnavailabilityReasonsRequestSuccess &&
      !isUnavailabilityReasonsRequestFailure &&
      retrieveUnavailabilityReasons();
  }, [
    isUnavailabilityReasonsRequestPending,
    isUnavailabilityReasonsRequestSuccess,
    isUnavailabilityReasonsRequestFailure,
  ]);

  return (
    <Modal header={t("unavailability.creation.editTitle")} close={cancel}>
      <div className={styles.header}>
        <div className={styles.contextualMenu}>
          <ContextualMenu
            items={[
              {
                name: t("unavailability.creation.remove"),
                renderIcon: (className) => (
                  <TrashIcon
                    className={classNames(className, styles.removeIcon)}
                  />
                ),
                color: "#db5f63",
                act: () =>
                  pushView({
                    key: UseCases.removeUnavailability,
                    data: unavailability,
                  }),
              },
            ]}
          />
        </div>
      </div>
      <form
        onSubmit={(event) => {
          event.preventDefault();

          !isRequestPending &&
            editUnavailability({
              unavailability,
              nextUnavailability: {
                ...formData,
                endDate: formData.endDate || formData.startDate,
              },
            });
        }}
        className={styles.unavailabilityEdition}
      >
        <div className={styles.main}>
          <div className={styles.form}>
            {loading ? (
              <Loader />
            ) : (
              <UnavailabilityEditionForm
                reasons={unavailabilityReasons}
                formData={formData}
                accountResource={account.resource}
                error={error}
                editForm={setFormData}
              />
            )}
          </div>
        </div>
        <div className={styles.buttons}>
          <Button type="cancel" press={cancel}>
            {t("unavailability.creation.cancel")}
          </Button>
          <Button type="submit" status={isRequestPending ? "pending" : "none"}>
            {t("unavailability.creation.edit")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export { UnavailabilityEdition };
